import type React from 'react';
import { useRef, useState } from 'react';
import type { DropEvent, FileRejection } from 'react-dropzone';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import IconMdi from '@components/IconMdi';
import InputText from '@components/InputText';
import { useEntityStore } from '@ContractBuilder/store';
import {
  AttachmentToast,
  uploadAttachmentPageContainerClasses,
  uploadAttachmentWrapperClasses,
  useAttachmentMutations,
} from '@features/attachments';
import { mdiFileDocumentOutline } from '@mdi/js';
import {
  hasAttachmentValidFileExt,
  hasAttachmentValidPDFMarker,
  hasAttachmentValidSize,
} from '@root/helpers/attachments/validation-files';
import { hasAttachmentValidPdfStructure } from '@root/helpers/attachments/validation-structure';
import { isPdfEncrypted } from '@root/helpers/pdf/is-pdf-encrypted';
import { useEffectOnce } from '@src/hooks';
import { isEndorsementPath, isEndorsementViewPath, isSubmissionPath, isTemplatePath } from '@utils/app-paths';
import clsx from 'clsx';

import { useBlockCreator } from '../../context/context';
import { StepView } from '../StepView';

interface UploadState {
  error?: string;
  isFileDropped: boolean;
  isUploading: boolean;
}

export const StepInlineAttachment = () => {
  const { onPrevStep, handleCancel } = useBlockCreator();
  const [attachmentName, setAttachmentName] = useState<string | undefined>(undefined);
  const [attachment, setAttachment] = useState<File | undefined>(undefined);

  const isEndorsementView = isEndorsementViewPath();

  const { submission } = useEntityStore(({ submission }) => ({ submission }));
  const { createInlineAttachment } = useAttachmentMutations();

  const templateId = isTemplatePath() ? submission?.id : undefined;
  const submissionId = isEndorsementPath() ? submission?.parentId : isSubmissionPath() ? submission?.id : undefined;
  const endorsementId = isEndorsementPath() ? submission?.id : undefined;

  const [uploadState, setUploadState] = useState<UploadState>({
    error: undefined,
    isFileDropped: false,
    isUploading: false,
  });

  const handleFileRejected = (fileRejections: FileRejection[], _event: DropEvent) => {
    const latestFileError = fileRejections?.pop()?.errors?.pop();
    const code = latestFileError?.code;
    let message = latestFileError?.message;

    if (code === 'file-too-small') {
      message = 'File is smaller than 4 bytes';
    }
    if (code === 'file-too-large') {
      message = 'File is larger than 10MB';
    }
    if (code === 'file-invalid-type') {
      message = 'File type must be pdf';
    }
    if (code === 'too-many-files') {
      message = 'Too many files';
    }

    setUploadState((current) => ({ ...current, error: message }));
  };

  const handleFileUpload = async (files: File[]) => {
    if (files.length < 1) {
      return;
    }

    setAttachment(files[0]);

    setUploadState((current) => ({ ...current, isFileDropped: true }));
  };

  const handleClickUpload = async () => {
    if (!attachment || uploadState.isUploading) {
      return;
    }

    setUploadState((current) => ({ ...current, error: undefined, isUploading: true }));

    try {
      const isEncrypted = await isPdfEncrypted(attachment);
      if (isEncrypted) {
        toast.error(
          <AttachmentToast
            title="Encrypted Attachment"
            description="This file is password protected. Please remove the password and re-upload."
          />,
        );
        return setUploadState((current) => ({
          ...current,
          error: 'This file is password protected. Please remove the password and re-upload.',
          isUploading: false,
        }));
      }

      if (
        !hasAttachmentValidSize(attachment) ||
        !hasAttachmentValidFileExt(attachment) ||
        !(await hasAttachmentValidPDFMarker(attachment)) ||
        !(await hasAttachmentValidPdfStructure(await attachment.arrayBuffer()))
      ) {
        toast.error(
          <AttachmentToast
            title="Corrupt Attachment"
            description="This file can't be processed. Please check the file and re-upload a valid version."
          />,
        );
        return setUploadState((current) => ({
          ...current,
          error: `This file can't be processed. Please check the file and re-upload a valid version.`,
          isUploading: false,
        }));
      }

      await createInlineAttachment.mutateAsync({
        attachment,
        attachmentName,
        endorsementId,
        submissionId,
        templateId,
        isEndorsementView,
      });
      toast.success('PDF attached successfully');
      handleCancel();
    } catch {
      setUploadState((current) => ({ ...current, error: 'Failed to upload', isUploading: false }));
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAttachmentName(e.target.value);
  };

  const inputRef = useRef<HTMLInputElement>(null);

  useEffectOnce(() => {
    inputRef.current?.focus();
  });

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
    },
    minSize: 5, // > 4 bytes
    maxSize: 10485759, // < 10 MB
    maxFiles: 1,
    onDrop: handleFileUpload,
    multiple: false,
    onDropRejected: handleFileRejected,
  });

  return (
    <StepView
      isValid={
        attachmentName !== undefined &&
        attachmentName?.length > 0 &&
        attachment !== undefined &&
        !uploadState.isUploading
      }
      isLoading={uploadState.isUploading}
      nextButtonLabel="Attach"
      onPrev={onPrevStep}
      secondHeaderTitle="Add attachment"
      stepId="inline-attachment"
      onNext={handleClickUpload}
    >
      <div className="mx-auto flex w-2/3 flex-col gap-7">
        <InputText
          name="create-attachment-name-input"
          dataTestId="text:create-attachment-name-input"
          labelText="Attachment name"
          value={attachmentName ?? ''}
          onChange={handleNameChange}
          placeholder="Attachment name"
          className="w-full"
          ref={inputRef}
        />
        {uploadState.isFileDropped && (
          <div className={clsx(uploadAttachmentPageContainerClasses)}>
            <div className={clsx(uploadAttachmentWrapperClasses, 'flex-col items-center')}>
              <div className="mt-2 inline-flex max-w-full items-center text-sm leading-5 font-normal text-gray-700">
                <IconMdi path={mdiFileDocumentOutline} />
                <div className="ml-1 h-5 overflow-hidden text-ellipsis whitespace-nowrap">
                  {attachment?.name || 'document.pdf'}
                </div>
              </div>
            </div>
          </div>
        )}
        {!uploadState.isFileDropped && (
          <div className={clsx(uploadAttachmentPageContainerClasses)}>
            <div {...getRootProps()} className={clsx(uploadAttachmentWrapperClasses, 'bg-white')}>
              <p className="mt-2 text-sm leading-5 font-normal text-gray-700">
                <span className="cursor-pointer text-[#2563EB]">Upload a file</span> or drag and drop
              </p>
              <p className="mt-2 text-[12px] leading-5 font-normal text-gray-400">PDF only, up to 10MB</p>
              <input
                {...getInputProps()}
                id="attachment-file-upload"
                name="attachment-file-upload"
                type="file"
                className="sr-only"
              />
              {uploadState.error && <p className="mt-4 text-[12px] text-red-500">{uploadState.error}</p>}
            </div>
          </div>
        )}
      </div>
    </StepView>
  );
};
